import type {
  NodeDto,
  TagDataCurrentResponseDto,
  TagDto,
  TagInformationDto,
  TagAndKeyRequest,
  NodeWithMetaAndKey,
  TagAttributeDto,
  TagDataKeyDto,
  SparkplugMetaDto,
  NodePublishType,
  TagUpdateDto,
  TagActiveInactiveResult,
} from "~/types/api";
import { BaseAPI } from "./base";
import type { ApiResponse } from "~/types/ui";

export class NodeApi extends BaseAPI {
  async getNodes(): Promise<ApiResponse<TagInformationDto[]>> {
    return this.get<TagInformationDto[]>(`companies/${this.company()}/tags`);
  }

  async getTag(deveui: string): Promise<ApiResponse<TagDto>> {
    return this.get<TagDto>(`tag/tagDto/${deveui}`);
  }

  async getAllAttributes(): Promise<ApiResponse<TagAttributeDto[]>> {
    return this.get<TagAttributeDto[]>(
      `tag/company/${this.company()}/attributes`,
    );
  }

  async getAllNodes(): Promise<ApiResponse<NodeDto[]>> {
    return this.get<NodeDto[]>(`tag/nodes/${this.company()}`);
  }

  async getActiveInactiveCount(): Promise<
    ApiResponse<TagActiveInactiveResult>
  > {
    return this.get<TagActiveInactiveResult>(
      `tag/active-inactive-count/${this.company()}`,
    );
  }

  async getCurrents(
    deveui: string,
  ): Promise<ApiResponse<TagDataCurrentResponseDto[]>> {
    return this.get<TagDataCurrentResponseDto[]>(`tag/${deveui}/currents`);
  }

  async getKeysForTag(deveui: string): Promise<ApiResponse<TagDataKeyDto[]>> {
    return this.get<TagDataKeyDto[]>(`tag/${deveui}/keys`);
  }

  async getMultipleCurrents(
    deveuis: string[],
  ): Promise<ApiResponse<Record<string, TagDataCurrentResponseDto[]>>> {
    return this.post<Record<string, TagDataCurrentResponseDto[]>>(
      `tag/currents/bulk`,
      deveuis.map((x) => {
        return { deveui: x };
      }),
    );
  }

  async createNode(
    applicationId: number,
    tag: TagDto,
  ): Promise<ApiResponse<void>> {
    return this.post<void>(`applications/${applicationId}/tag`, tag);
  }

  async createNodes(tags: TagDto[]): Promise<ApiResponse<void>> {
    return this.post<void>(`tag/bulk`, tags);
  }

  async createOrUpdateNodes(tags: TagDto[]): Promise<ApiResponse<void>> {
    return this.put<void>(`tag/bulk`, tags);
  }

  async deleteNode(deveui: string): Promise<ApiResponse<void>> {
    return this.delete<void>(`tag/${deveui}`);
  }

  async deleteNodes(deveuis: TagDto[]): Promise<ApiResponse<void>> {
    return this.post<void>(`tag/bulk-delete`, deveuis);
  }

  async getNodeSensorMeta(
    nodes: TagAndKeyRequest[],
  ): Promise<ApiResponse<Record<string, NodeWithMetaAndKey>>> {
    return this.post<Record<string, NodeWithMetaAndKey>>(
      `tag/meta-with-keys`,
      nodes,
    );
  }

  async updateTag(
    deveui: string,
    payload: TagUpdateDto,
  ): Promise<ApiResponse<void>> {
    return this.put<void>(`tag/${deveui}`, payload);
  }

  async createOrUpdateSensors(
    deveui: string,
    payload: TagDataKeyDto[],
  ): Promise<ApiResponse<void>> {
    return this.post<void>(`tag/${deveui}/key/bulk`, payload);
  }

  async createOrUpdateSparkplug(
    deveui: string,
    payload: SparkplugMetaDto,
  ): Promise<ApiResponse<void>> {
    return this.put<void>(`tag/${deveui}/sparkplug`, payload);
  }

  async deletePublishing(
    deveui: string,
    publishingType: NodePublishType,
  ): Promise<ApiResponse<void>> {
    return this.delete<void>(`tag/publishing/${deveui}/${publishingType}`);
  }
}
